import { createElement, FC, Fragment, ReactNode, useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Badge, Button, Divider, Flex, Popconfirm, Typography } from 'antd';
import { TbDeviceDesktop, TbDeviceDesktopMinus, TbLogout, TbWorld, TbWorldPin } from 'react-icons/tb';
import { toast } from '@/shared/components/toasts/PlatformToast';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useMaps } from '@/hooks/useMaps';
import {
    EmptySection,
    LearnMoreFooter,
    MiddleDot,
    SmallTag,
    SmallTooltip,
    VirtualizedList as List,
} from '@/shared';
import { BaseSkeleton } from '@/shared/components/skeletons';
import { useAuthStore } from '@/stores/auth';
import { useSessionStore } from '@/stores/database/entities/00-RBAC/99-Sessions';
import { imgLogoStyling } from '@/theme';
import { Session } from '@/types';
import { formatDate } from '@/utilities';

import Metadata from './meta';

const Sessions: FC = (): JSX.Element => {
    const { token } = useAntDesignTheme();
    const { browserEngineToSVGMap, applicationTypeToIconMap } = useMaps();
    const { sessions, loading, fetchSessions, revokeSession, revokeOtherSessions } = useSessionStore();
    const [revokingId, setRevokingId] = useState<string>();
    const [revokingOthers, setRevokingOthers] = useState(false);

    useEffect(() => {
        void fetchSessions();
    }, [fetchSessions]);

    const sortedSessions = useMemo(
        () =>
            [...(sessions ?? [])].sort(
                (a, b) =>
                    Number(new Date(b.lastSeenAt ?? b.updatedAt)) - Number(new Date(a.lastSeenAt ?? a.updatedAt)),
            ),
        [sessions],
    );
    const currentSession = sortedSessions.find((session) => session.isCurrent);

    const logoutClient = () => {
        localStorage.removeItem('token');
        useAuthStore.getState().setAccessToken('');
        window.location.replace('/login');
    };

    const handleRevokeSession = async (session: Session) => {
        if (!session.id) {return;}

        setRevokingId(session.id);

        try {
            const response = await revokeSession(session.id);

            toast(response.wasCurrent ? 'Session Ended' : 'Session Revoked', {
                icon: createElement(response.wasCurrent ? TbLogout : TbDeviceDesktopMinus, { size: '1rem' }),
                description: response.wasCurrent
                    ? 'You have been securely logged out of this browser.'
                    : `${session.browser ?? session.applicationType} will be required to authenticate again.`,
            });

            if (response.wasCurrent) {logoutClient();}
        } catch (error) {
            captureException(error);
            toast.error('Unable to revoke session');
        } finally {
            setRevokingId(undefined);
        }
    };

    const handleRevokeOtherSessions = async () => {
        setRevokingOthers(true);

        try {
            const response = await revokeOtherSessions();

            toast('Other Sessions Revoked', {
                icon: createElement(TbDeviceDesktopMinus, { size: '1rem' }),
                description: `${response.revokedSessionCount.toLocaleString()} ${
                    response.revokedSessionCount === 1 ? 'session was' : 'sessions were'
                } revoked.`,
            });
        } catch (error) {
            captureException(error);
            toast.error('Unable to revoke other sessions');
        } finally {
            setRevokingOthers(false);
        }
    };

    const renderSession = (session: Session) => {
        const lastSeenAt = session.lastSeenAt ?? session.updatedAt;
        const browserIcon = session.browser ? browserEngineToSVGMap[session.browser] : undefined;
        const title = [session.browser, session.os].filter(Boolean).join(' on ') || `${session.applicationType} Session`;
        const metadataItems = ([
            session.loginMethod,
            <>Last active {formatDate(lastSeenAt)}</>,
            session.expiresAt ? <>Expires {formatDate(session.expiresAt)}</> : null,
            session.location || session.ipAddress ? (
                <SmallTooltip
                    placement='bottom'
                    title={
                        <>
                            <TbWorldPin
                                size='1rem'
                                style={{ marginRight: '0.325rem', verticalAlign: '-0.2rem' }}
                            />
                            {session.ipAddress ?? 'IP unavailable'}
                        </>
                    }
                >
                    {session.location ?? 'Location unavailable'}
                </SmallTooltip>
            ) : null,
            session.deviceType,
        ] as ReactNode[]).filter((item) => item !== null && item !== undefined && item !== '');

        return (
            <List.Item
                key={session.id}
                extra={
                    <Popconfirm
                        arrow={false}
                        cancelText='Cancel'
                        description={
                            session.isCurrent
                                ? 'This browser will return to the login screen.'
                                : 'This device will be required to authenticate again.'
                        }
                        okButtonProps={{ danger: true, loading: revokingId === session.id }}
                        okText={session.isCurrent ? 'Log Out' : 'Revoke'}
                        onConfirm={() => handleRevokeSession(session)}
                        placement='bottomRight'
                        title={session.isCurrent ? 'Log out of this session?' : 'Revoke this session?'}
                    >
                        <Button
                            danger
                            disabled={Boolean(revokingId)}
                            icon={
                                session.isCurrent ? (
                                    <TbLogout size='1rem' style={{ verticalAlign: '-0.175rem' }} />
                                ) : (
                                    <TbDeviceDesktopMinus size='1rem' style={{ verticalAlign: '-0.175rem' }} />
                                )
                            }
                            loading={revokingId === session.id}
                            size='small'
                            type='text'
                        >
                            {session.isCurrent ? 'Log Out' : 'Revoke'}
                        </Button>
                    </Popconfirm>
                }
            >
                <List.Item.Meta
                    avatar={
                        <Badge
                            count={
                                <Avatar
                                    icon={applicationTypeToIconMap[session.applicationType] ?? <TbWorld size='75%' />}
                                    shape='square'
                                    size={16}
                                    style={{
                                        backgroundColor: token.colorBgContainer,
                                        border: `1px solid ${token.colorBorder}`,
                                    }}
                                />
                            }
                            offset={[-2, 36]}
                        >
                            <Avatar
                                icon={
                                    browserIcon ? (
                                        <img
                                            alt={`${session.browser} browser`}
                                            src={browserIcon}
                                            style={{ ...imgLogoStyling, transform: 'scale(0.75)' }}
                                        />
                                    ) : (
                                        (applicationTypeToIconMap[session.applicationType] ?? (
                                            <TbWorld style={{ color: token.colorTextTertiary }} />
                                        ))
                                    )
                                }
                                shape='square'
                                size='large'
                                style={{ border: `1px solid ${token.colorBorder}`, borderRadius: token.borderRadius }}
                            />
                        </Badge>
                    }
                    title={
                        <Flex align='center' gap='small'>
                            <Typography.Text strong>{title}</Typography.Text>
                            {session.isCurrent && (
                                <SmallTag
                                    message='Current Session'
                                    styles={{
                                        backgroundColor: token.colorPrimaryBg,
                                        color: token.colorPrimary,
                                        marginLeft: 0,
                                    }}
                                />
                            )}
                        </Flex>
                    }
                    description={
                        <div style={{ color: token.colorTextTertiary }}>
                            {metadataItems.map((item, index) => (
                                <Fragment key={`session-metadata-${index}`}>
                                    {index > 0 && <MiddleDot horizontalMargin='0.25rem' />}
                                    {item}
                                </Fragment>
                            ))}
                        </div>
                    }
                />
            </List.Item>
        );
    };

    return (
        <>
            <Metadata />
            <Flex align='flex-start' justify='space-between' gap='large'>
                <div>
                    <Typography.Title>Sessions</Typography.Title>
                    <Typography.Paragraph style={{ color: token.colorTextTertiary }}>
                        Review and revoke the sessions for the account associated with this workspace here.
                    </Typography.Paragraph>
                </div>
            </Flex>
            <Divider style={{ margin: '1rem 0' }} />
            <Typography.Title id='settings-account-sessions-current' level={4}>
                Current Sessions
            </Typography.Title>
            <Typography.Paragraph style={{ color: token.colorTextTertiary }}>
                Review active devices, recent activity, and session expiration details.
            </Typography.Paragraph>
            <div
                style={{
                    border: `1px dashed ${token.colorBorder}`,
                    borderRadius: token.borderRadius * 1.5,
                    overflow: 'hidden',
                }}
            >
                {sortedSessions.length > 0 && !loading ? (
                    <List
                        bordered={false}
                        dataSource={sortedSessions}
                        estimateSize={84}
                        getItemKey={(session: Session) => session.id}
                        height='25rem'
                        renderItem={renderSession}
                        size='small'
                    />
                ) : loading ? (
                    <div style={{ minHeight: '25rem', padding: '1rem' }}>
                        <BaseSkeleton active round paragraph={{ rows: 10 }} />
                    </div>
                ) : (
                    <EmptySection
                        description='No active cookie sessions were found for this account in the current workspace.'
                        height='25rem'
                        icon={<TbDeviceDesktop style={{ color: token.colorTextTertiary }} />}
                        label='No Active Sessions'
                    />
                )}
                {sortedSessions.length > 1 && currentSession && (
                    <>
                        <Divider style={{ margin: 0 }} />
                        <div style={{ padding: '0.75rem' }}>
                            <Popconfirm
                                arrow={false}
                                cancelText='Cancel'
                                description='Every other device will be required to authenticate again. This browser will remain active.'
                                okButtonProps={{ danger: true, loading: revokingOthers }}
                                okText='Revoke Others'
                                onConfirm={handleRevokeOtherSessions}
                                placement='bottomRight'
                                title='Revoke all other sessions?'
                            >
                                <Button
                                    block
                                    danger
                                    icon={
                                        <TbDeviceDesktopMinus
                                            size='1rem'
                                            style={{ verticalAlign: '-0.175rem' }}
                                        />
                                    }
                                    loading={revokingOthers}
                                    size='small'
                                    type='dashed'
                                >
                                    Revoke Other Sessions
                                </Button>
                            </Popconfirm>
                        </div>
                    </>
                )}
            </div>
            <Divider style={{ margin: '1rem 0' }} />
            <LearnMoreFooter topic='Sessions' />
        </>
    );
};

export default Sessions;
